import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import 'font-awesome/css/font-awesome.min.css';

import {
    listRegistrations,
    listPairs,
    getAmericana,
    autoAssignPairs,
    removeRegistrationById,
    removeCourt,
    deleteAmericana,
} from '../api/americanas';
import Breadcrumb from '../components/Breadcrumb';
import AmericanaRegisterForm from '../components/AmericanaRegisterForm';
import AmericanaPairsEditor from '../components/AmericanaPairsEditor';
import AmericanaGuestForm from '../components/AmericanaGuestForm';
import './AmericanaDetails.css';
import usePlayersDirectory from './americanaDetails/usePlayersDirectory';
import { formatRegistrationName as formatGuestRegistrationName } from './americanaDetails/guestUtils';
import {
    normalizeRegistration,
    mergePreservingGuest,
    groupRegistrationsByStatus,
    buildStatusSections,
    countParticipants,
} from './americanaDetails/registrationUtils';
import { useCurrentPlayer } from "../hooks/useCurrentPlayer";

const ORGANIZER_ROLES = ['ROLE_CAPTAIN', 'ROLE_COORDINATOR'];

const extractCapacity = (americana) => {
    if (!americana) return null;
    const rawCapacity = americana.maxPlayers ?? americana.max ?? americana.capacity;
    const numeric = Number(rawCapacity);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const regKey = (r) => (r?.playerId != null ? `p:${r.playerId}` : r?.guestId != null ? `g:${r.guestId}` : null);
const keyFromPairSide = (pair, side) => {
    const pId = pair?.[`player${side}Id`];
    if (pId != null) return `p:${pId}`;
    const gId = pair?.[`guest${side}Id`];
    if (gId != null) return `g:${gId}`;
    const participant = pair?.[`participant${side}`];
    if (participant?.id != null && participant?.type) return `${String(participant.type).toLowerCase()}:${participant.id}`;
    return null;
};

const toRoleWithPrefix = (r) => {
    if (!r) return null;
    const u = String(r).toUpperCase();
    return u.startsWith('ROLE_') ? u : `ROLE_${u}`;
}

const normalizeRoles = (rs) => {
    if (!Array.isArray(rs)) return [];
    return rs.map(toRoleWithPrefix).filter(Boolean);
}

export default function AmericanaDetails({ currentUserId, isAdmin, roles = [] }) {
    const { id } = useParams();
    const americanaId = Number(id);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [autoAssign, setAutoAssign] = useState(false);
    const [registrations, setRegistrations] = useState([]);
    const [pairs, setPairs] = useState([]);
    const [pairsAssignedBy, setPairsAssignedBy] = useState(null);
    const [americana, setAmericana] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const { playersById, formatPlayerName } = usePlayersDirectory();
    const { player, loading: playerLoading } = useCurrentPlayer();

    const myplayerId = player?.id ?? player?.player?.id ?? currentUserId ?? null;
    const propsRoles = normalizeRoles(roles);
    const roleFromHook = toRoleWithPrefix(player?.role);
    const hookRoles = roleFromHook ? [roleFromHook] : [];
    const effectiveRoles = propsRoles.length ? propsRoles : hookRoles;
    const effectiveIsAdmin = typeof isAdmin === 'boolean'
        ? isAdmin
        : effectiveRoles.some(r => ['ROLE_ADMIN', 'ROLE_COORDINATOR', 'ROLE_CAPTAIN'].includes(r));

    const normalizedRoles = useMemo(() => effectiveRoles, [effectiveRoles]);
    const canOrganize = useMemo(() => {
        if (effectiveIsAdmin) return true;
        return normalizedRoles.some((role) => ORGANIZER_ROLES.includes(role));
    }, [effectiveIsAdmin, normalizedRoles]);

    const canRegisterOthers = useMemo(
        () => effectiveIsAdmin || normalizedRoles.some((role) => ORGANIZER_ROLES.includes(role)),
        [effectiveIsAdmin, normalizedRoles]
    );

    const normalizeAll = useCallback((items) => (items ?? []).map(normalizeRegistration), []);

    const load = useCallback(async () => {
        setError(null);
        setLoading(true);
        try {
            const [info, registrationsResponse, pairsResponse] = await Promise.all([
                getAmericana(americanaId),
                listRegistrations(americanaId),
                listPairs(americanaId),
            ]);
            setAmericana(info);
            const normalizedRegistrations = normalizeAll(registrationsResponse);
            setRegistrations((previous) =>
                previous?.length ? mergePreservingGuest(normalizedRegistrations, previous) : normalizedRegistrations
            );
            setPairs(pairsResponse);
        } catch (e) {
            setError(e?.response?.data?.message || 'Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, [americanaId, normalizeAll]);

    const refreshRegistrations = useCallback(async () => {
        setError(null);
        try {
            const [updatedRegistrations, updatedPairs] = await Promise.all([
                listRegistrations(americanaId),
                listPairs(americanaId),
            ]);
            setRegistrations(normalizeAll(updatedRegistrations));
            setPairs(updatedPairs);
        } catch (e) {
            console.error(e);
        }
    }, [americanaId, normalizeAll]);

    useEffect(() => {
        if (!pairs || pairs.length === 0) setPairsAssignedBy(null);
    }, [pairs]);

    useEffect(() => { load(); }, [load]);

    const registrationsByStatus = useMemo(() => groupRegistrationsByStatus(registrations), [registrations]);
    const statusSections = useMemo(() => buildStatusSections(registrationsByStatus), [registrationsByStatus]);
    const confirmedParticipants = useMemo(() => countParticipants(registrationsByStatus.confirmed), [registrationsByStatus]);

    const formatRegistrationName = useCallback(
        (registration) => formatGuestRegistrationName(registration, formatPlayerName),
        [formatPlayerName]
    );

    const registrationByKey = useMemo(() => {
        const map = new Map();
        for (const r of registrations) {
            const k = regKey(r);
            if (k) map.set(k, r);
        }
        return map;
    }, [registrations]);

    const nameFromPairSide = useCallback((pair, side) => {
        const k = keyFromPairSide(pair, side);
        if (k && registrationByKey.has(k)) return formatRegistrationName(registrationByKey.get(k));
        const pId = pair?.[`player${side}Id`];
        if (pId != null) return formatPlayerName(pId);
        const gId = pair?.[`guest${side}Id`];
        if (gId != null) return 'Invitada';
        return '—';
    }, [registrationByKey, formatRegistrationName, formatPlayerName]);

    const groupedPairs = useMemo(() => {
        const map = new Map();
        for (const pair of pairs) {
            if (!map.has(pair.court)) map.set(pair.court, []);
            map.get(pair.court).push(pair);
        }
        return [...map.entries()]
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([court, courtPairs]) => ({
                court,
                pairs: [...courtPairs].sort((a, b) => a.pairSlot - b.pairSlot),
            }));
    }, [pairs]);

    const capacity = useMemo(() => extractCapacity(americana), [americana]);
    const summary = useMemo(() => ({
        confirmed: confirmedParticipants,
        waitlist: registrationsByStatus.waitlist.length,
        cancelled: registrationsByStatus.cancelled.length,
    }), [confirmedParticipants, registrationsByStatus]);

    const expectedCourts = useMemo(() => {
        if (!capacity) return null;
        return Math.max(1, Math.ceil(capacity / 4));
    }, [capacity]);

    const handleConfirmDelete = async () => {
        setDeleteError(null);
        setDeleting(true);
        try {
            await deleteAmericana(americanaId);
            setDeleting(false);
            setDeleteDialogOpen(false);
            navigate('/americanas');
        } catch (err) {
            setDeleting(false);
            setDeleteError('No se pudo eliminar la americana.');
        }
    };

    const handleAutoAssign = async () => {
        const seed = Date.now();
        setAutoAssign(true);
        try {
            const confirmedCount = registrationsByStatus.confirmed?.length ?? 0;
            const courtsByConfirmed = Math.max(1, Math.ceil(confirmedCount / 4));
            const numCourts = expectedCourts ? Math.max(1, Math.min(expectedCourts, courtsByConfirmed)) : courtsByConfirmed;

            await autoAssignPairs(americanaId, numCourts, seed);
            await load();
            setPairsAssignedBy('auto');
        } catch (e) {
            setError('Error al crear parejas automáticas.');
        } finally {
            setAutoAssign(false);
        }
    };

    const handleRemoveRegistration = async (reg) => {
        try {
            await removeRegistrationById(americanaId, reg.id);
            await load();
        } catch (e) { setError('Error al eliminar inscripción'); }
    };

    const handleRemoveCourt = async (court) => {
        try {
            await removeCourt(americanaId, court);
            await load();
        } catch (e) { setError('Error al eliminar pista'); }
    };

    if (loading) return (
        <div className="americana-details-wrapper">
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando americana...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="americana-details-wrapper">
            <div className="error-banner">
                <i className="fa fa-exclamation-triangle"></i> {error}
            </div>
        </div>
    );

    return (
        <div className="americana-details-wrapper">
            <div className="americana-details-container">

                <header className="americana-header">
                    <Breadcrumb crumbs={[
                        { label: 'Americanas', to: '/americanas' },
                        { label: americana?.name || 'Americana' },
                    ]} />

                    {(effectiveIsAdmin || canOrganize) && (
                        <div className="header-actions">
                            {effectiveIsAdmin && (
                                <button className="icon-btn" title="Editar" onClick={() => navigate(`/americanas/${americanaId}/edit`)}>
                                    <i className="fa fa-pencil"></i>
                                </button>
                            )}
                            {canOrganize && (
                                <button className="icon-btn delete" title="Eliminar" onClick={() => setDeleteDialogOpen(true)}>
                                    <i className="fa fa-trash"></i>
                                </button>
                            )}
                        </div>
                    )}

                    <div className="header-content">
                        <span className="americana-subtitle">Detalles del Evento</span>
                        <h1 className="americana-title">{americana?.name || `Americana #${americanaId}`}</h1>

                        <div className="meta-row">
                            {americana?.dateTime && (
                                <div className="meta-item">
                                    <i className="fa fa-calendar"></i>
                                    {new Date(americana.dateTime).toLocaleString()}
                                </div>
                            )}
                            {americana?.location && (
                                <div className="meta-item">
                                    <i className="fa fa-map-marker"></i>
                                    {americana.location}
                                </div>
                            )}
                        </div>

                        <div className="summary-grid">
                            {capacity != null && (
                                <div className="stat-card">
                                    <span className="stat-label">Cupo Total</span>
                                    <span className="stat-value">{capacity}</span>
                                </div>
                            )}
                            <div className="stat-card highlight">
                                <span className="stat-label">Confirmadas</span>
                                <span className="stat-value">{summary.confirmed}</span>
                            </div>
                            <div className="stat-card">
                                <span className="stat-label">En Espera</span>
                                <span className="stat-value">{summary.waitlist}</span>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="sections-grid">

                    <div className="content-card registration-section">
                        <div className="card-header">
                            <h2>Inscripción</h2>
                            <p>Gestiona tu plaza o añade jugadoras.</p>
                        </div>

                        <AmericanaRegisterForm
                            americanaId={americanaId}
                            currentUserId={myplayerId}
                            isAdmin={effectiveIsAdmin}
                            canRegisterOthers={canRegisterOthers}
                            onRegistered={load}
                            capacity={capacity}
                            confirmedCount={confirmedParticipants}
                        />

                        {effectiveIsAdmin && (
                            <div style={{ marginTop: '20px' }}>
                                <AmericanaGuestForm
                                    americanaId={americanaId}
                                    capacity={capacity}
                                    confirmedCount={confirmedParticipants}
                                    onGuestAdded={() => setTimeout(() => refreshRegistrations(), 500)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="content-card status-section">
                        <div className="card-header">
                            <h2>Listado</h2>
                            <p>Jugadoras inscritas.</p>
                        </div>

                        {statusSections.map((section) => {
                            const entries = registrationsByStatus[section.key] || [];
                            if (entries.length === 0) return null;

                            return (
                                <div key={section.key} className="status-block">
                                    <div className="status-header">
                                        <h3>{section.title}</h3>
                                        <span className="badge-count">{entries.length}</span>
                                    </div>
                                    <div className="players-list">
                                        {entries.map((reg) => (
                                            <div key={reg.id} className="player-row">
                                                <span>{formatRegistrationName(reg)}</span>
                                                {effectiveIsAdmin && (
                                                    <button className="remove-btn-small" onClick={() => handleRemoveRegistration(reg)}>
                                                        <i className="fa fa-times-circle"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {(effectiveIsAdmin || canOrganize) && (
                    <div className="content-card pairs-section">
                        <div className="card-header" style={{display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'10px'}}>
                            <div>
                                <h2>Organización</h2>
                                <p>Asignación de pistas y parejas.</p>
                            </div>
                            {canOrganize && (
                                <button
                                    className="action-button" // Estilo global o definido anteriormente
                                    style={{width:'auto', padding:'8px 16px', fontSize:'0.9rem'}}
                                    onClick={handleAutoAssign}
                                    disabled={autoAssign}
                                >
                                    {autoAssign ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-magic"></i>}
                                    {' '}Auto-Asignar
                                </button>
                            )}
                        </div>

                        {groupedPairs.length > 0 ? (
                            <div className="pairs-grid">
                                {groupedPairs.map(({ court, pairs: courtPairs }) => (
                                    <div key={court} className="court-box">
                                        <div className="court-header">
                                            <span>Pista {court}</span>
                                            {effectiveIsAdmin && (
                                                <button className="remove-btn-small" onClick={() => handleRemoveCourt(court)}>
                                                    <i className="fa fa-trash-o"></i>
                                                </button>
                                            )}
                                        </div>
                                        {courtPairs.map((pair) => (
                                            <div key={`${court}-${pair.pairSlot}`} className="pair-item">
                                                <span>{nameFromPairSide(pair, 1)}</span>
                                                <span className="vs-divider">+</span>
                                                <span>{nameFromPairSide(pair, 2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{textAlign:'center', padding:'20px', color:'var(--text-muted)', fontStyle:'italic'}}>
                                No hay parejas asignadas todavía.
                            </div>
                        )}

                        {effectiveIsAdmin && (
                            <div style={{marginTop: '20px', borderTop: '1px dashed #e2e8f0', paddingTop: '20px'}}>
                                <AmericanaPairsEditor
                                    americanaId={americanaId}
                                    onSaved={() => { setPairsAssignedBy('manual'); load(); }}
                                    capacity={capacity}
                                    expectedCourts={expectedCourts}
                                    initialPairs={pairs}
                                    allowedRegistrations={registrationsByStatus.confirmed}
                                    playersById={playersById}
                                    formatRegistrationNameFn={formatRegistrationName}
                                    disabled={pairsAssignedBy === 'auto'}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {deleteDialogOpen && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h3 className="modal-title">¿Eliminar Americana?</h3>
                        <p className="modal-text">Esta acción no se puede deshacer y se perderán todas las inscripciones.</p>

                        {deleteError && <div className="error-banner" style={{marginBottom:'15px'}}>{deleteError}</div>}

                        <div className="modal-actions">
                            <button className="btn-modal cancel" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                                Cancelar
                            </button>
                            <button className="btn-modal danger" onClick={handleConfirmDelete} disabled={deleting}>
                                {deleting ? 'Eliminando...' : 'Sí, Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}